<template>
  <div class="docx-viewer-popup" :style="overlayBgStyle" @click="$emit('closeDocxViewer')">
    <div class="close-button">
      <q-btn icon="close" flat round dense @click="$emit('closeDocxViewer')" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>
    <div class="docx-viewer-container">
      <div class="docx-content" ref="docxContainerRef">
        <!-- Le contenu du DOCX sera rendu ici -->
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import axios from "axios";
import mammoth from 'mammoth'; // Importation de mammoth.js
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

export default {
  components: { ReturnHomeButton },
  props: {
    docxResource: { // Renommé pour être plus spécifique que blockScrollDatas
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const docxContainerRef = ref(null); // Référence à l'élément conteneur du DOCX


    const closeDocxViewer = () => {
      emit('closeDocxViewer');
    }

    const closeAllDialogs = () => {
      emit('closeAllDialogs');
    }

    const fetchAndRenderDocx = async () => {
      let resourceToProcess = null;

      if (props.docxResource && 
          props.docxResource.associatedResources && 
          Array.isArray(props.docxResource.associatedResources) && 
          props.docxResource.associatedResources.length > 0) {
        resourceToProcess = props.docxResource.associatedResources[0];
        console.log("Utilisation de la ressource associée:", resourceToProcess);
      } else if (props.docxResource && props.docxResource.name && props.docxResource.name.toLowerCase().endsWith('.docx')) {
        // Fallback: si associatedResources n'est pas là mais que docxResource.name est un .docx
        // Cela pourrait être utile si parfois le DocxViewer est appelé avec une ressource directe.
        resourceToProcess = props.docxResource; 
        console.warn("Utilisation de props.docxResource.name comme ressource DOCX (fallback):", resourceToProcess);
      } else {
        console.error("Ressource DOCX invalide ou nom de fichier manquant dans associatedResources. Props reçues:", props.docxResource);
        if (docxContainerRef.value) docxContainerRef.value.innerHTML = "<p style='color: red;'>Ressource DOCX non valide ou non spécifiée dans les ressources associées.</p>";
        return;
      }

      if (!resourceToProcess || !resourceToProcess.name) {
        console.error("Nom de fichier de la ressource à traiter est manquant.");
        if (docxContainerRef.value) docxContainerRef.value.innerHTML = "<p style='color: red;'>Nom de fichier de la ressource DOCX manquant.</p>";
        return;
      }

      if (!docxContainerRef.value) {
        console.error("La référence au conteneur DOCX n'est pas prête.");
        return;
      }

      docxContainerRef.value.innerHTML = "<p>Chargement du document...</p>";

      try {
        const s3ResourceInfoUrl = `${process.env.VUE_APP_BASEURL}/s3Resource/${resourceToProcess.name}`;
        console.log("Récupération des informations de la ressource DOCX depuis:", s3ResourceInfoUrl);
        const s3ResourceInfoResponse = await axios.get(s3ResourceInfoUrl);
        const resourceDataString = s3ResourceInfoResponse.data;

        console.log("Données de ressource reçues (chaîne attendue - URL ou base64):", typeof resourceDataString, resourceDataString.substring(0,100) + "...");

        let arrayBufferToProcess;

        if (typeof resourceDataString === 'string' && resourceDataString.startsWith('http')) {
          console.log("La ressource est une URL HTTP. Utilisation du proxy pour la récupérer comme ArrayBuffer.");
          const proxyUrl = `${process.env.VUE_APP_BASEURL}/proxy-document?url=${encodeURIComponent(resourceDataString)}`;
          console.log("Appel du proxy:", proxyUrl);
          const proxyResponse = await axios.get(proxyUrl, { responseType: 'arraybuffer' });
          arrayBufferToProcess = proxyResponse.data;
          console.log("ArrayBuffer reçu du proxy. Longueur:", arrayBufferToProcess.byteLength);
        } else if (typeof resourceDataString === 'string') {
          console.log("La ressource est traitée comme une chaîne base64.");
          let dataUrl = resourceDataString;
          if (!resourceDataString.startsWith('data:')) {
            const mimeType = resourceToProcess.name.toLowerCase().endsWith('.docx') 
              ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              : 'application/msword';
            dataUrl = `data:${mimeType};base64,${resourceDataString}`;
            console.log("Data URL construite:", dataUrl.substring(0,100) + "...");
          }
          const fetchResponse = await fetch(dataUrl);
          const blob = await fetchResponse.blob();
          arrayBufferToProcess = await blob.arrayBuffer();
          console.log("ArrayBuffer obtenu depuis base64 via fetch/blob. Longueur:", arrayBufferToProcess.byteLength);
        } else {
          console.error("Format de données de la ressource DOCX non géré. Attendu: chaîne (URL ou base64). Reçu:", typeof resourceDataString, resourceDataString);
          if(docxContainerRef.value) docxContainerRef.value.innerHTML = "<p style='color: red;'>Format de données du document non géré.</p>";
          return;
        }

        if (!arrayBufferToProcess) {
            console.error("Aucun ArrayBuffer n'a pu être préparé pour Mammoth.");
            if(docxContainerRef.value) docxContainerRef.value.innerHTML = "<p style='color: red;'>Impossible de préparer les données du document.</p>";
            return;
        }
        
        console.log("Préparation pour mammoth.convertToHtml. arrayBufferToProcess est un ArrayBuffer:", arrayBufferToProcess instanceof ArrayBuffer, "Longueur:", arrayBufferToProcess.byteLength);
        const firstBytes = new Uint8Array(arrayBufferToProcess.slice(0, 4));
        console.log("Premiers 4 octets de l'ArrayBuffer final:", firstBytes);

        const result = await mammoth.convertToHtml({ arrayBuffer: arrayBufferToProcess });
        if(docxContainerRef.value) docxContainerRef.value.innerHTML = result.value;
        const messages = result.messages;
        if (messages && messages.length > 0) {
          console.warn("Messages du rendu DOCX:", messages);
        }

      } catch (error) {
        console.error("Erreur lors du chargement ou du rendu du fichier DOCX:", error);
        if (docxContainerRef.value) {
          docxContainerRef.value.innerHTML = "<p style='color: red;'>Erreur lors du chargement du document.</p>";
          if (error.message && error.message.includes("zip file")) {
            docxContainerRef.value.innerHTML += "<p style='font-size: smaller; color: grey;'>Le fichier est peut-être corrompu ou n'est pas un .docx valide.</p>";
          }
        }
      }
    };

    onMounted(() => {
      fetchAndRenderDocx();
    });

    // Si l'URL du DOCX peut changer, surveillez les props
    watch(() => props.docxResource, () => {
      fetchAndRenderDocx();
    }, { deep: true });

    const { overlayBgStyle } = useOverlayBg();

    return {
      overlayBgStyle,
      docxContainerRef,
      closeDocxViewer,
      closeAllDialogs
    };
  },
};
</script>

<style scoped>
.docx-viewer-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(240, 240, 240, 0.95); /* Fond légèrement différent pour distinguer */
  display: flex;
  flex-direction: column; /* Pour placer le bouton de fermeture au-dessus */
  justify-content: center;
  align-items: center;
  z-index: 10000; /* Un z-index élevé */
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.docx-viewer-container {
  width: 80%;
  max-width: 900px; /* Largeur maximale pour la lisibilité */
  height: 85vh;    /* Hauteur de la vue */
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  padding: 20px;
  overflow-y: auto; /* Permettre le scroll si le contenu est long */
}

.docx-content {
  /* Les styles spécifiques au contenu du DOCX seront appliqués par la librairie
     ou peuvent être ajoutés ici pour surcharger/améliorer */
  line-height: 1.6;
}

/* Styles pour les éléments DOCX (à adapter en fonction du rendu de la librairie) */
.docx-content >>> h1, .docx-content >>> h2, .docx-content >>> h3 {
  color: #333;
}
.docx-content >>> p {
  margin-bottom: 1em;
}
/* Ajoutez ici d'autres styles globaux si nécessaire pour le contenu DOCX */
</style> 